import { useDataProvider, type DataProvider, type Identifier } from "ra-core";
import { useCallback } from "react";

import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Contact, PipelineCard, Priority } from "../../types";
import {
  splitFullName,
  withPrimaryEmail,
  withPrimaryPhone,
} from "../cardModel";
import { getTopPosition, newItemPosition } from "../positions";
import type { NewDeal } from "./DealsSection";
import type { DraftTask } from "./DraftTasksSection";

export interface CardDraft {
  pipelineId: Identifier;
  stageId: Identifier;
  name: string;
  tagIds: number[];
  email: string;
  phone: string;
  salesId: Identifier | undefined;
  source: string | null;
  priority: Priority;
  tasks: DraftTask[];
  deals: NewDeal[];
  files: File[];
  notes: string[];
}

const getTopOfStage = async (
  dataProvider: DataProvider,
  pipelineId: Identifier,
  stageId: Identifier,
) => {
  const { data } = await dataProvider.getList<PipelineCard>("pipeline_cards", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "position", order: "ASC" },
    filter: { pipeline_id: pipelineId, stage_id: stageId },
  });
  return getTopPosition(data);
};

/**
 * Creates the contact, its card at the top of the stage, then the
 * negotiations, tasks, notes and documents typed in the create panel.
 */
export const useCreateCard = () => {
  const dataProvider = useDataProvider();
  const { dealStages } = useConfigurationContext();

  return useCallback(
    async (draft: CardDraft): Promise<CreatedCard> => {
      const now = new Date().toISOString();
      const { data: contact } = await dataProvider.create<Contact>("contacts", {
        data: {
          ...splitFullName(draft.name),
          email_jsonb: withPrimaryEmail([], draft.email),
          phone_jsonb: withPrimaryPhone([], draft.phone),
          tags: draft.tagIds,
          sales_id: draft.salesId,
          source: draft.source,
          first_seen: now,
          last_seen: now,
        },
      });

      const { data: card } = await dataProvider.create<PipelineCard>(
        "pipeline_cards",
        {
          data: {
            pipeline_id: draft.pipelineId,
            stage_id: draft.stageId,
            contact_id: contact.id,
            priority: draft.priority,
            position: await getTopOfStage(
              dataProvider,
              draft.pipelineId,
              draft.stageId,
            ),
          },
        },
      );

      // The card exists from here: a failing extra must not hide it
      const results = await Promise.allSettled([
        ...draft.deals.map((deal) =>
          dataProvider.create("deals", {
            data: {
              name: deal.name,
              amount: deal.amount ?? 0,
              description: deal.description || null,
              contact_ids: [contact.id],
              company_id: null,
              stage: dealStages[0]?.value ?? "opportunity",
              sales_id: draft.salesId,
              index: 0,
            },
          }),
        ),
        ...draft.tasks.map((task) =>
          dataProvider.create("tasks", {
            data: {
              text: task.text,
              due_date: task.due ? new Date(task.due).toISOString() : null,
              contact_id: contact.id,
              sales_id: draft.salesId,
              type: "none",
              status: "todo",
              priority: "low",
              position: newItemPosition(),
            },
          }),
        ),
        ...draft.notes.map((text) =>
          dataProvider.create("contact_notes", {
            data: {
              contact_id: contact.id,
              text,
              date: now,
              sales_id: draft.salesId,
            },
          }),
        ),
        ...(draft.files.length > 0
          ? [
              dataProvider.create("contact_notes", {
                data: {
                  contact_id: contact.id,
                  text: "",
                  date: now,
                  sales_id: draft.salesId,
                  attachments: draft.files.map(toAttachment),
                },
              }),
            ]
          : []),
      ]);
      const failures = results.filter((result) => result.status === "rejected");
      failures.forEach((failure) =>
        console.error("Failed to save a card item", failure.reason),
      );

      return { card, failedItems: failures.length };
    },
    [dataProvider, dealStages],
  );
};

export interface CreatedCard {
  card: PipelineCard;
  /** Negotiations, tasks, notes or documents that could not be saved. */
  failedItems: number;
}

/** Shape expected by the providers' attachment upload (`RAFile`). */
export const toAttachment = (file: File) => ({
  rawFile: file,
  src: URL.createObjectURL(file),
  title: file.name,
  type: file.type,
});
